import { Capacitor } from '@capacitor/core';
import { haversineKm } from '@/lib/geo';

/**
 * Localização do aparelho — fonte única pra Home, onboarding e a trava de
 * "eletroposto errado" do ConfigureCharge.
 *
 * Por que existe (bug 2026-10-08, docs/bugs/2026-10-08-localizacao-gps-desligado.md):
 * com o GPS do celular DESLIGADO, o plugin `@capacitor/geolocation` não
 * devolve um estado — ele REJEITA `checkPermissions()`/`requestPermissions()`
 * com "Location services are not enabled" (OS-PLUG-GLOC-0007). O app tratava
 * a rejeição como "indisponível" e escondia o pedido em todo lugar; sem
 * posição, a Home destacava o primeiro eletroposto da lista do backend como
 * se fosse o mais próximo e clientes iniciavam recarga na estação errada.
 *
 *  - 'granted'     → permissão dada, precisa OU aproximada (Android 12+ deixa
 *                    escolher; aproximada já separa eletropostos a km)
 *  - 'prompt'      → ainda dá pra mostrar o pedido do sistema
 *  - 'denied'      → só pelos Ajustes do aparelho
 *  - 'off'         → localização do aparelho desligada (vale pra qualquer app)
 *  - 'unavailable' → shell antigo sem o plugin / navegador sem geolocalização
 */
export type LocationState = 'granted' | 'prompt' | 'denied' | 'off' | 'unavailable';

export interface Coords {
  lat: number;
  lng: number;
}

export interface Fix extends Coords {
  /** Raio de incerteza em metros (null quando a plataforma não informa). */
  accuracyM: number | null;
}

export interface LocationResult {
  state: LocationState;
  fix: Fix | null;
}

// Posição "morna" serve pra ordenar eletropostos: aceita cache de até 5 min e
// não liga o GPS de alta precisão (economia de bateria).
const WARM_OPTS = { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 } as const;
// Pra trava do ConfigureCharge a posição precisa ser de AGORA: quem saiu de
// casa há 5 min ainda teria a coordenada de casa no cache.
const FRESH_OPTS = { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 } as const;

// Códigos do plugin (iguais em Android e iOS).
const ERR_LOCATION_OFF = 'OS-PLUG-GLOC-0007';
const ERR_ENABLE_REFUSED = 'OS-PLUG-GLOC-0009'; // Android: "Agora não" no diálogo do Google
const ERR_NETWORK_AND_LOCATION_OFF = 'OS-PLUG-GLOC-0017';

// NUNCA retornar o proxy do plugin através de `await` (o await acessa `.then`
// no proxy → "Geolocation.then() is not implemented"). Sempre desestruturar
// do módulo no ponto de uso: `const { Geolocation } = await geolocationModule()`.
async function geolocationModule() {
  return import('@capacitor/geolocation');
}

export function locationSupported(): boolean {
  if (Capacitor.isNativePlatform()) return Capacitor.isPluginAvailable('Geolocation');
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

export function isLocationOffError(err: unknown): boolean {
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code?: unknown }).code)
      : '';
  if (
    code === ERR_LOCATION_OFF ||
    code === ERR_ENABLE_REFUSED ||
    code === ERR_NETWORK_AND_LOCATION_OFF
  ) {
    return true;
  }
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /location services are not enabled/i.test(message);
}

/** Estado do plugin → estado do app. Aproximada basta (ver topo do arquivo). */
export function classifyPermission(location?: string, coarseLocation?: string): LocationState {
  if (location === 'granted' || coarseLocation === 'granted') return 'granted';
  // Android só devolve 'denied' quando não dá mais pra perguntar (2ª recusa);
  // 'prompt-with-rationale' ainda mostra o pedido do sistema.
  if (location === 'denied' && (coarseLocation === undefined || coarseLocation === 'denied')) {
    return 'denied';
  }
  return 'prompt';
}

/** Consulta sem mostrar pedido nenhum — seguro pra rodar no mount. */
export async function checkLocationState(): Promise<LocationState> {
  if (!locationSupported()) return 'unavailable';
  try {
    const { Geolocation } = await geolocationModule();
    const { location, coarseLocation } = await Geolocation.checkPermissions();
    return classifyPermission(location, coarseLocation);
  } catch (err) {
    if (isLocationOffError(err)) return 'off';
    // Permissions API ausente (Safari antigo) ou erro do plugin: "dá pra pedir"
    // — o pedido em si decide. Nunca esconder o caminho de ativação.
    return 'prompt';
  }
}

function toFix(pos: { coords: { latitude: number; longitude: number; accuracy?: number } }): Fix {
  const acc = pos.coords.accuracy;
  return {
    lat: pos.coords.latitude,
    lng: pos.coords.longitude,
    accuracyM: typeof acc === 'number' && Number.isFinite(acc) ? acc : null
  };
}

/** Lê a posição SEM pedir nada (chamar só com o estado 'granted'). */
export async function readFix(fresh = false): Promise<Fix | null> {
  try {
    const { Geolocation } = await geolocationModule();
    return toFix(await Geolocation.getCurrentPosition(fresh ? FRESH_OPTS : WARM_OPTS));
  } catch {
    return null;
  }
}

/**
 * Pede a localização — SÓ a partir de um toque do usuário.
 *
 * Nativo: `requestPermissions()` primeiro (no Android 12+ é o pedido com a
 * opção "Precisa", que o Wi-Fi de 1 toque aproveita); com o GPS desligado ele
 * rejeita e quem resolve é o `getCurrentPosition`, que no Android pede a
 * permissão E abre o diálogo nativo do Google "Ativar localização". Se o
 * cliente recusar o pedido, NÃO emenda um segundo pedido — no Android a 2ª
 * recusa vira "não perguntar de novo".
 */
export async function requestLocation(fresh = false): Promise<LocationResult> {
  if (!locationSupported()) return { state: 'unavailable', fix: null };
  try {
    const { Geolocation } = await geolocationModule();
    if (Capacitor.isNativePlatform()) {
      try {
        const { location, coarseLocation } = await Geolocation.requestPermissions();
        const state = classifyPermission(location, coarseLocation);
        if (state !== 'granted') return { state, fix: null };
      } catch (err) {
        if (!isLocationOffError(err)) throw err;
        // GPS desligado → segue pro getCurrentPosition (diálogo do Google).
      }
    }
    // Web: o próprio getCurrentPosition dispara o pedido do navegador.
    const fix = toFix(await Geolocation.getCurrentPosition(fresh ? FRESH_OPTS : WARM_OPTS));
    return { state: 'granted', fix };
  } catch (err) {
    if (isLocationOffError(err)) return { state: 'off', fix: null };
    // A permissão pode ter sido dada e só a leitura falhado (timeout em
    // ambiente fechado) — o estado real decide o que a tela mostra.
    return { state: await checkLocationState(), fix: null };
  }
}

/** Ajustes do app dá pra abrir só quando o shell tem o plugin do scanner. */
export function canOpenAppSettings(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('BarcodeScanner');
}

/**
 * Abre a tela de Ajustes do app (onde mora a permissão de Localização).
 * Reaproveita `BarcodeScanner.openSettings()` — o plugin já está nos shells
 * publicados, então funciona por OTA, sem dependência nova nem build de loja.
 */
export async function openAppSettings(): Promise<boolean> {
  if (!canOpenAppSettings()) return false;
  try {
    const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
    await BarcodeScanner.openSettings();
    return true;
  } catch {
    return false;
  }
}

// ── Trava de "eletroposto errado" ───────────────────────────────────────────

/** Mais longe que isso do eletroposto = pede confirmação. O par de estações
 * mais próximo (Serra Yahoo × Serra Day by Day) fica a ~5 km. */
export const FAR_FROM_SITE_KM = 1.5;
/** Posição com incerteza maior que isso não serve pra julgar (torre de celular
 * em área aberta chega a vários km). */
export const MAX_TRUSTED_ACCURACY_M = 3_000;

export type SiteCheck =
  | { kind: 'near'; distanceKm: number }
  | { kind: 'far'; distanceKm: number }
  | { kind: 'unknown' };

/**
 * Onde o cliente está em relação ao eletroposto. A incerteza da leitura é
 * descontada da distância, então só dá 'far' quando ele está longe MESMO.
 */
export function checkSite(fix: Fix | null, site: Coords | null): SiteCheck {
  if (!fix || !site) return { kind: 'unknown' };
  if (fix.accuracyM !== null && fix.accuracyM > MAX_TRUSTED_ACCURACY_M) {
    return { kind: 'unknown' };
  }
  const distanceKm = haversineKm(fix.lat, fix.lng, site.lat, site.lng);
  const slackKm = (fix.accuracyM ?? 0) / 1000;
  return distanceKm - slackKm > FAR_FROM_SITE_KM
    ? { kind: 'far', distanceKm }
    : { kind: 'near', distanceKm };
}

/** lat/lng do backend (string decimal ou null) → Coords, ou null se inválido. */
export function siteCoords(
  latitude: string | null | undefined,
  longitude: string | null | undefined
): Coords | null {
  if (latitude == null || longitude == null) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}
